import React from 'react';
import Component from '../../dispatcher/modules/component/Component';
import { NavigationBaseGrid, PuzzleComboBox, PuzzleInput } from '../index';
import { Renderers } from './EditorGridRenderers';
import CellText from './cell_renderer/CellText';
import _ from 'lodash';
import { ComboBoxFilterChangeEvent } from '@progress/kendo-react-dropdowns';
import {
  NAVIGATION_BASE_EDITOR_GRID_PROPS,
  NAVIGATION_BASE_EDITOR_GRID_STATE
} from './Types';

class NavigationBaseEditorGrid extends Component <NAVIGATION_BASE_EDITOR_GRID_PROPS, NAVIGATION_BASE_EDITOR_GRID_STATE> {
  cellEdit = {
    field: undefined,
    value: undefined,
    uniqueField: undefined,
    valid: undefined,
    errorMessage: undefined
  }

  state: NAVIGATION_BASE_EDITOR_GRID_STATE = {
    data: [],
    editField: undefined,
    currentFilterValue: ''
  };

  private gridRef: NavigationBaseGrid;
  protected activeCellInput: HTMLInputElement | string;

  constructor(props) {
    super(props);
  }

  /**
   * Adds row to grid
   * @param {Object} dataItem
   * @param {String} editField
   */
  addRow = (dataItem = {}, editField?: string) => {
    let data = [...this.gridRef.state.data];
    data.push({
      ...dataItem,
      inEdit: editField
    });
    data = this.gridRef.getDisabledAllCheckboxData(data).data;
    this.activeCellInput = 'addRowAction';
    let selectedState = {};
    if (this.props.selectableAll) {
      selectedState = { ...this.gridRef.state.selectedState };
      selectedState[this.gridRef.getIdGetter()(data[data.length - 1])] = dataItem[this.props.selectableAll];
    }
    if (_.isFunction(this.props.onAddRow)) {
      this.props.onAddRow(dataItem, {
        editField,
        data,
        selectedState
      });
    }
    this.gridRef.addedNewRecord = { ...dataItem, Index: data.length - 1 };

    this.setState({
      editField: editField
    });
    this.gridRef.setState({
      data,
      selectedState
    }, () => {
      if (editField) {
        const column = this.gridRef.findColumn(editField);
        const { validation } = column;
        this.setCellEdit(dataItem[editField], editField, dataItem, validation, true);
      }
    });
  }

  /**
   * Starts edit grid cell
   * @param {Object[]} dataItem
   * @param {String} field
   */
  private enterEdit = (dataItem: any, field: string) => {
    const { uniqueField } = this.props;

    const data = this.gridRef.state.data.map((item: any) => ({
      ...item,
      inEdit: item[uniqueField] === dataItem[uniqueField] ? field : undefined
    }));

    this.cellEdit = {
      value: dataItem[field],
      field: field,
      uniqueField: dataItem.uniqueField,
      valid: dataItem.isAllFieldValid,
      errorMessage: _.get(Object.values(dataItem?.allValidation || {}), '[0].errorMessage')
    };
    this.gridRef.setState({
      data,
      editField: field
    });
  };

  /**
   * Exits edit grid cell
   * @param {Object[]} dataItem
   */
  private exitEdit = (dataItem?: any) => {
    let dataItemRelatedTarget;
    if (dataItem?.isRelatedTarget) {
      dataItemRelatedTarget = { ...dataItem };
      dataItem = undefined;
    }
    const { columns } = this.props;
    dataItem = dataItem ? dataItem : this.cellEdit;
    const columnItem = columns.find(item => item.field === dataItem.field) || {};
    const { onBeforeExitEdit } = columnItem;
    const { value, field, uniqueField } = dataItem;
    let continueExit = true;
    if (onBeforeExitEdit) {
      continueExit = onBeforeExitEdit(dataItem, dataItemRelatedTarget);
    }
    if (continueExit) {
      const data = this.gridRef.state.data.map((item: any) => {
        if (item[this.props.uniqueField] === uniqueField) {
          item[field] = value;
        }
        return {
          ...item,
          inEdit: undefined
        };
      });

      if (_.isFunction(this.props.onExitEdit)) {
        this.props.onExitEdit(value, {
          field,
          uniqueField,
          data
        });
      }

      this.setState({ editField: undefined });
      this.gridRef.setState({ data });
    }
  };

  /**
   * Sets NavigationBaseGrid reference
   * @param {NavigationBaseGrid} el
   */
  private setGridReference = (el: any) => {
    this.gridRef = el;
  };

  /**
   * Dispatcher load grid method
   * @param {Object} data
   * @param forceSkip
   */
  public setGridData = (data: any, forceSkip?: boolean) => {
    this.props.setGridData(data, forceSkip);
  };

  /**
   * Returns grid data
   */
  getGridData = () => this.gridRef.getGridData()

  onActivateCellInput = (input: HTMLInputElement) => {
    if (this.activeCellInput === 'addRowAction') {
      this.activeCellInput = input;
      this.activeCellSelect();
    } else {
      this.activeCellInput = input;
    }
  }
  activeCellSelect = () => {
    // @ts-ignore
    this.activeCellInput?.select && this.activeCellInput.select();
  }

  /**
   * Get editable fields and validation
   */
  getEditableFields = () => this.props.columns.filter(column => column.editable && column.validation)
    .map(column => ({
      field: column.field,
      validation: column.validation
    }));

  /**
   * Returns column object by cellIndex of td tag
   * @param {Number} cellIndex
   */
  getColumnByCellIndex = (cellIndex) => {
    const { columns } = this.gridRef.state;
    let visibleColumnIndex = -1;
    return columns.find((item) => {
      if (!item.hidden) {
        visibleColumnIndex++;
      }
      return cellIndex === visibleColumnIndex;
    });
  }

  /**
   * Handles the onFilterChange event.
   * @param {Object} event - The event object containing the filter value.
   * @param {string} event.filter.value - The current value of the search filter.
   */
  onSearchChange = (event:ComboBoxFilterChangeEvent) => {
    const { value } = event.filter;
    this.setState({
      currentFilterValue: value
    });
  };

  /**
   * On row deleted handler
   * @param dataItem
   * @param additional
   */
  onDeleteRow = (dataItem, additional) => {
    const { updatedData } = additional;
    const currentEditorState = { ...this.state.currentEditorState };
    if (this.cellEdit.field && this.cellEdit.uniqueField === this.gridRef.getIdGetter()(dataItem)) {
      this.cellEdit = {
        field: undefined,
        value: undefined,
        uniqueField: undefined,
        valid: undefined,
        errorMessage: undefined
      };
      currentEditorState.valid = true;
    }

    const editableFields = this.getEditableFields();
    let currentEditorStateAllValid = true;

    const dataWithValidation = updatedData.map((item) => {
      let validationObj = {};
      item.isAllFieldValid = true;

      editableFields.forEach(({ field, validation }) => {
        validationObj[field] = validation(item[field], field, updatedData, item);
        if (validationObj[field].valid === false) {
          item.isAllFieldValid = false;
          currentEditorStateAllValid = false;
        }
      });

      item.allValidation = validationObj;
      return item;
    });

    this.props.onFieldsValidation?.(currentEditorStateAllValid);
    this.gridRef.setState({
      data: dataWithValidation
    });

    this.props.onDeleteRow?.(dataItem, additional);
  }

  /**
   * On edit cell change handler
   * @param value
   * @param field
   * @param dataItem
   * @param validation
   */
  onEditCellChange = (value: any, field, dataItem, validation?: Function) => {
    this.setCellEdit(value, field, dataItem, validation);
  };

  /**
   * Handler for active cell editing
   * @param value
   * @param field
   * @param dataItem
   * @param validation
   * @param isNewRowAdded
   */
  setCellEdit = (value: any, field, dataItem, validation?: Function, isNewRowAdded: boolean = false) => {
    const { data } = this.gridRef.state;
    const uniqueFieldOfDataItem = dataItem[this.props.uniqueField];

    const newData = data.map((item) => {
      if (uniqueFieldOfDataItem === item[this.props.uniqueField]) {
        item[field] = value;
      }

      return item;
    });

    let currentEditorStateAllValid = true;

    const dataWithValidation = newData.map((item) => {
      let validationObj = {
        valid: true,
        errorMessage: ''
      };

      if (_.isFunction(validation)) {
        validationObj = validation(item[field], field, data, item);
      }

      if (_.isUndefined(item.allValidation)) {
        item.allValidation = {};
      }

      item.allValidation[field] = {
        valid: validationObj.valid,
        errorMessage: validationObj.errorMessage
      };

      const allValidationArray = Object.values(item.allValidation);
      item.isAllFieldValid = allValidationArray.every(item => item['valid'] === true);

      if (item.isAllFieldValid === false) {
        currentEditorStateAllValid = false;
      }

      if (uniqueFieldOfDataItem === item[this.props.uniqueField]) {
        this.cellEdit = {
          value,
          field,
          uniqueField: uniqueFieldOfDataItem,
          valid: validationObj.valid,
          errorMessage: validationObj.errorMessage
        };
      }

      return item;
    });

    if (_.isFunction(validation)) {
      this.props.onFieldsValidation?.(currentEditorStateAllValid);

      this.gridRef.setState({
        data: dataWithValidation
      });
    }
  }

  /**
   * Returns if column is editable
   * @param {String} field
   */
  isFieldEditable = (field) => {
    const column = this.props.columns.find((column) => {
      const { cellType, dataIndex, field: columnField } = column;
      return (_.isUndefined(cellType) ? (columnField || dataIndex) : dataIndex) === field;
    });
    return column?.editable;
  }

  /**
   * Opened cell editor renderer
   * @param column
   * @param valueItem
   * @param itemValidation
   * @param cellProps
   */
  renderCellEditor = (column, valueItem, itemValidation, cellProps) => {
    const { dataItem, field } = cellProps;
    const { editorConfig, comboData, editable, validation, allowCustom, clearButton, showTooltip, errorMessageToolTip, trimValue } = column;
    const { editorName, filterable } = editorConfig ?? {};
    const { inEdit } = dataItem;
    let content: React.ReactElement = (
      <span 
        aria-hidden={'true'}
      >{valueItem}</span>
    );
    if (editorName === 'combo') {
      const isValid = this.state.currentFilterValue.trim() !== '' ? true : this.cellEdit.valid;
      content = (
        <PuzzleComboBox
          onChange={(e: any) => {
            let value = e?.target?.value;
            if (trimValue && _.isString(value)) {
              value = value.trim().replace(/ +/g, ' ');
            }

            this.onEditCellChange(value, field, dataItem, validation);
            if (e.nativeEvent.key === 'Enter') {
              this.exitEdit();
            }
          }}
          allowCustom={allowCustom}
          clearButton={clearButton}
          defaultValue={valueItem}
          filterable={filterable}
          onFilterChange={this.onSearchChange}
          data={comboData}
          valid={errorMessageToolTip ? itemValidation?.valid : isValid}
          showTooltip={showTooltip}
          errorMessageToolTip={errorMessageToolTip}
          errorTxt={errorMessageToolTip ? itemValidation?.errorMessage : undefined}
        />);
    } else {
      content = (
        <PuzzleInput
          onChange={(e) => {
            const value = e?.target?.value;
            this.onEditCellChange(value, field, dataItem, validation);
          }}
          onBlur={(e) => {
            let value = e?.target?.value;
            if (trimValue && _.isString(value)) {
              value = e.target.value.trim().replace(/ +/g, ' ');
              this.onEditCellChange(value, field, dataItem, validation);
            }
          }}
          onKeyDown={(e) => {
            if (e.event.key === 'Enter') {
              if (trimValue && _.isString(e?.target?.value)) {
                const value = e.target.value.trim().replace(/ +/g, ' ');
                this.onEditCellChange(value, field, dataItem, validation);
              }
              this.exitEdit();
            }
          }}
          defaultValue={valueItem}
          valid={this.cellEdit.valid}
        />
      );
    }

    return (
      <td
        aria-colindex={cellProps.ariaColumnIndex}
        className={`${cellProps.className} tp-in-edit`}
        {...this.renderers.getOpenedEditableFieldProps()}
        tabIndex={-1}
      >
        {content}
      </td>
    );
  }

  /**
   * On field focus handler
   * @param _event
   * @param dataItem
   * @param field
   */
  onFocusField = (_event, dataItem, field) => {
    this.enterEdit(dataItem, field);
  }

  /**
   * Editor cell renderer
   * @param cellProps
   * @param column
   * @param valueItem
   */
  editorCellRender = (cellProps, column, valueItem):JSX.Element => {
    const { dataItem, field } = cellProps;
    const { errorMessageToolTip } = column;
    const { inEdit, allValidation } = dataItem;
    const itemValidation = allValidation?.[field];

    if (inEdit === field) {
      return this.renderCellEditor(column, valueItem, itemValidation, cellProps);
    }

    if (errorMessageToolTip && itemValidation?.valid === false) {
      const cellConfig = _.cloneDeep(column?.cellConfig || {});
      const tdAdditionalProps = this.renderers.getClosedEditableFieldProps(dataItem, field);
      return (
        <CellText
          {...cellProps}
          isErrorMessageToolTip
          editable
          cellConfig={{
            ...cellConfig,
            parentClassName: 'tp-cell-error k-cursor-pointer',
            className: `${cellConfig?.className ? cellConfig?.className : ''} tp-cell-error-text`,
            additionalProps: tdAdditionalProps
          }}
          uniqueField={this.props.uniqueField}
        />
      );
    }

    return (
      <td
        aria-colindex={cellProps.ariaColumnIndex}
        className={`${cellProps.className} k-cursor-pointer`}
        tabIndex={0}
        onFocus={e => this.onFocusField(e, dataItem, field)}
      >
        {valueItem}
      </td>
    );
  }

  /**
   * Mixin class methods
   */
  private renderers = new Renderers(this.enterEdit, this.exitEdit, 'inEdit', this.onActivateCellInput, this.getColumnByCellIndex, this.isFieldEditable);

  renderBody() {
    const { height, className = '' } = this.props;
    return (
      <NavigationBaseGrid
        style={{ height: height }}
        {...this.props}
        className={`tp-editor-grid ${className}`}
        rowRender={this.renderers.rowRender}
        navigatable={true}
        ref={this.setGridReference}
        customCellRender={this.editorCellRender}
      />
    );
  }
}

export default NavigationBaseEditorGrid;
